<template>
  <div v-if="page_loading">
    <Loading />
  </div>
  <div v-else>
    <!-- Shopid : 8985 -->
    <v-card v-if="!isAllowShop" elevation="1" class="v-card-bottom-30">
      <v-card-text class="text-center py-10">
        <v-icon size="60" color="grey lighten-1">mdi-lock-outline</v-icon>
        <div class="mt-3 fn-16">{{ use_language.no_permission_this_page }}</div>
      </v-card-text>
    </v-card>

    <div v-else>
      <!-- ===== Filter ===== -->
      <v-card elevation="1" class="v-card-bottom-30">
        <v-card-title class="body-1">
          <v-icon color="primary" class="mr-2">mdi-clipboard-list</v-icon>
          {{ use_language.sales_tracking }}
          <v-spacer />
        </v-card-title>
        <v-divider />
        <v-card-text>
          <v-row class="pb-2">
            <v-col cols="12" md="3" class="py-1">
              <span class="fn-13">{{ use_language.search }}</span>
              <v-text-field
                v-model="filter.searchText"
                outlined dense hide-details
                :placeholder="use_language.customer_name_or_phone"
                prepend-inner-icon="mdi-magnify"
                @keyup.enter="searchData(1)"
              />
            </v-col>

            <v-col cols="12" md="2" class="py-1">
              <span class="fn-13">{{ use_language.date_should_call }} ({{ use_language.start_ }})</span>
              <v-menu v-model="menuDateFrom" :close-on-content-click="false" transition="scale-transition" offset-y min-width="290px">
                <template v-slot:activator="{ on }">
                  <v-text-field
                    :value="filter.callDateFrom"
                    outlined dense hide-details readonly clearable
                    :placeholder="use_language.select_date"
                    prepend-inner-icon="mdi-calendar"
                    v-on="on"
                    @click:clear="filter.callDateFrom = null"
                  />
                </template>
                <v-date-picker v-model="filter.callDateFrom" @input="menuDateFrom = false" locale="th" />
              </v-menu>
            </v-col>

            <v-col cols="12" md="2" class="py-1">
              <span class="fn-13">{{ use_language.date_should_call }} ({{ use_language.to_ }})</span>
              <v-menu v-model="menuDateTo" :close-on-content-click="false" transition="scale-transition" offset-y min-width="290px">
                <template v-slot:activator="{ on }">
                  <v-text-field
                    :value="filter.callDateTo"
                    outlined dense hide-details readonly clearable
                    :placeholder="use_language.select_date"
                    prepend-inner-icon="mdi-calendar"
                    v-on="on"
                    @click:clear="filter.callDateTo = null"
                  />
                </template>
                <v-date-picker v-model="filter.callDateTo" @input="menuDateTo = false" locale="th" />
              </v-menu>
            </v-col>

            <v-col cols="12" md="2" class="py-1">
              <span class="fn-13">{{ use_language.call_status }}</span>
              <v-select
                v-model="filter.isCalled"
                :items="callStatusList"
                item-text="text"
                item-value="value"
                outlined dense hide-details
              />
            </v-col>

            <v-col cols="12" md="3" class="py-1 d-flex align-end">
              <v-btn color="primary" class="mr-2" :loading="loading" @click="searchData(1)">
                <v-icon left small>mdi-magnify</v-icon> {{ use_language.search }}
              </v-btn>
              <v-btn outlined @click="clearFilter()">{{ use_language.clear_value }}</v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- ===== ตาราง ===== -->
      <v-card elevation="1">
        <v-card-title class="body-1">
          {{ use_language.items_to_follow_up }}
          <v-spacer />
          <span class="fn-13 color-grey">{{ use_language.all_ }} {{ formatMoney(resultCount) }} {{ use_language.item_unit }}</span>
        </v-card-title>
        <v-divider />
        <v-card-text>
          <v-data-table
            :headers="headers"
            :items="dataList"
            :loading="loading"
            item-key="id"
            hide-default-footer
            :items-per-page="perPage"
            class="elevation-0 packhai-border-table"
            :no-data-text="use_language.data_not_found"
            :loading-text="use_language.loading_dots"
          >
            <template v-slot:[`item.no`]="{ index }">
              <span class="color-grey">{{ (page - 1) * perPage + index + 1 }}</span>
            </template>
            <template v-slot:[`item.orderMasterID`]="{ item }">
              <a
                v-if="item.orderMasterID"
                class="text-decoration-none font-weight-bold"
                @click="openOrderDetail(item.orderMasterID)"
              >
                PA{{ item.orderMasterID }}
              </a>
              <span v-else class="color-grey">-</span>
            </template>
            <template v-slot:[`item.customerName`]="{ item }">
              <span class="font-weight-medium">{{ item.customerName || '-' }}</span>
            </template>
            <template v-slot:[`item.customerPhone`]="{ item }">
              <a v-if="item.customerPhone" :href="'tel:' + item.customerPhone" class="text-decoration-none black--text font-weight-bold">
              {{ item.customerPhone }}
              </a>
              <span v-else>-</span>
            </template>

            <template v-slot:[`item.deliveredDate`]="{ item }">
              {{ formatDate(item.deliveredDate) }}
            </template>

            <template v-slot:[`item.expectedRunOutDate`]="{ item }">
              {{ formatDate(item.expectedRunOutDate) }}
            </template>

            <template v-slot:[`item.callDate`]="{ item }">
              <span :class="callDateClass(item)">{{ formatDate(item.callDate) }}</span>
            </template>

            <template v-slot:[`item.daysUntilCall`]="{ item }">
              <span v-if="item.daysUntilCall == null" class="color-grey">-</span>
              <span v-else :class="daysTextClass(item)">{{ daysText(item) }}</span>
            </template>

            <template v-slot:[`item.totalOrderCount`]="{ item }">
              {{ item.totalOrderCount }} {{ use_language.times }}
            </template>

            <template v-slot:[`item.isCalled`]="{ item }">
              <v-chip
                v-if="item.subscriptionStartDate"
                small color="info" text-color="white"
                :title="use_language.start_ + ' Subscription ' + formatDate(item.subscriptionStartDate)"
              >
                <v-icon left x-small>mdi-autorenew</v-icon>Subscription
              </v-chip>
              <v-chip v-else-if="!item.id" small outlined color="grey">{{ use_language.no_items_yet }}</v-chip>
              <v-chip v-else small :color="item.isCalled ? 'success' : 'warning'" text-color="white">
                {{ item.isCalled ? use_language.called : use_language.not_called }}
              </v-chip>
            </template>

            <template v-slot:[`item.action`]="{ item }">
              <v-btn small text color="success" @click="openCallDialog(item)">
                <v-icon small left>mdi-phone-check</v-icon>
                {{ item.id ? use_language.save_information : use_language.view_data }}
              </v-btn>
            </template>
          </v-data-table>

          <v-row v-if="pageLength > 1" class="text-center pa-2 ma-0">
            <v-col cols="12" class="pa-0">
              <v-pagination
                v-model="page"
                :length="pageLength"
                :total-visible="9"
                color="error"
                @input="searchData(page)"
              />
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </div>

    <!-- ===== Dialog บันทึกการโทร ===== -->
    <v-dialog v-model="callDialog.show" max-width="1200px" scrollable persistent>
      <v-card class="call-dialog">

        <div class="call-dialog__header">
          <v-icon color="grey darken-2" class="mr-2">mdi-phone-check</v-icon>
          <span class="grey--text text--darken-4 font-weight-medium">{{ use_language.log_call }}</span>
          <v-spacer />
          <v-btn icon small color="grey darken-1" @click="callDialog.show = false"><v-icon small>mdi-close</v-icon></v-btn>
        </div>

        <v-card-text class="pt-4">
          <v-row>

            <!-- ============ ซ้าย ============ -->
            <v-col cols="12" md="7">
              <v-card outlined class="mb-3">
                <div class="d-flex align-center px-4 py-3">
                  <div class="flex-grow-1" style="min-width:0;">
                    <div class="fn-15 font-weight-bold text-truncate cust-line"><v-icon small color="black" class="mr-1">mdi-account</v-icon>{{ callDialog.customerName || '-' }}</div>
                    <a
                      v-if="callDialog.customerPhone"
                      :href="'tel:' + callDialog.customerPhone"
                      class="text-decoration-none primary--text fn-14 font-weight-medium cust-line"
                    >
                      <v-icon small color="primary" class="mr-1">mdi-phone</v-icon>{{ callDialog.customerPhone }}
                    </a>
                    <span v-else class="fn-13 color-grey">{{ use_language.no_phone_number }}</span>
                  </div>
                  <div class="text-center flex-shrink-0 ml-2">
                    <div class="fn-18 font-weight-bold primary--text">{{ callDialog.orders.length }}</div>
                    <div class="fn-11 color-grey">{{ use_language.menu_orders }}</div>
                  </div>
                  <v-divider vertical class="mx-3" />
                  <div class="text-center flex-shrink-0">
                    <div class="fn-18 font-weight-bold" style="color:#f57c00;">{{ formatMoney(callDialog.orderTotalValue) }}</div>
                    <div class="fn-11 color-grey">{{ use_language.total_purchase }}</div>
                  </div>
                  <v-divider vertical class="mx-3" />
                  <div class="text-center flex-shrink-0">
                    <div class="fn-18 font-weight-bold success--text">{{ callDialog.calledCount }}</div>
                    <div class="fn-11 color-grey">{{ use_language.call_count }}</div>
                  </div>
                </div>

                <v-divider />
                <template v-if="callDialog.id">
                  <!-- วันที่ควรโทร (แก้ไขได้) -->
                  <div class="d-flex align-center px-4 py-1">
                    <v-icon small color="grey" class="mr-3">mdi-calendar-clock</v-icon>
                    <span class="fn-13 flex-grow-1">{{ use_language.date_should_call }}</span>
                    <v-menu
                      v-model="callDateMenu"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      offset-y min-width="290px"
                    >
                      <template v-slot:activator="{ on }">
                        <v-btn small text color="primary" :loading="callDialog.callDateSaving" v-on="on">
                          <v-icon left small>mdi-pencil</v-icon>{{ formatDate(callDialog.callDate) }}
                        </v-btn>
                      </template>
                      <v-date-picker
                        :value="callDatePickerValue"
                        locale="th"
                        @input="onCallDatePicked"
                      />
                    </v-menu>
                  </div>

                  <div class="d-flex align-center px-4 py-1">
                    <v-icon small :color="callDialog.isCalled ? 'success' : 'grey'" class="mr-3">
                      {{ callDialog.isCalled ? 'mdi-check-circle' : 'mdi-clock-outline' }}
                    </v-icon>
                    <span class="fn-13 flex-grow-1">
                      <template v-if="callDialog.subscriptionStartDate">
                        {{ callDialog.isCalled ? use_language.new_order_opened : use_language.no_new_order_opened }}
                      </template>
                      <template v-else>
                        {{ callDialog.isCalled ? use_language.called : use_language.not_called_yet }}
                      </template>
                    </span>
                    <v-switch
                      :key="switchKey"
                      :input-value="callDialog.isCalled"
                      color="success"
                      hide-details inset dense
                      class="mt-0 pt-0 flex-shrink-0"
                      :loading="callDialog.calledSaving"
                      @change="setIsCalled($event)"
                    />
                  </div>
                </template>

                <!-- Subscription -->
                <div
                  class="d-flex align-center px-4 py-2"
                  :class="{ 'sub-row--on': callDialog.subscriptionStartDate }"
                >
                  <v-icon small :color="callDialog.subscriptionStartDate ? 'info' : 'grey'" class="mr-3">
                    {{ callDialog.subscriptionStartDate ? 'mdi-autorenew' : 'mdi-autorenew-off' }}
                  </v-icon>
                  <span class="fn-13 flex-grow-1" style="min-width:0;">
                    {{ callDialog.subscriptionStartDate ? use_language.subscribed : use_language.not_yet_done + ' Subscription' }}
                    <span v-if="callDialog.subscriptionStartDate" class="fn-12 color-grey">
                      · {{ use_language.start_ }} {{ formatDate(callDialog.subscriptionStartDate) }}
                    </span>
                  </span>

                  <v-btn
                    v-if="callDialog.subscriptionStartDate"
                    small text color="error"
                    :loading="callDialog.subSaving"
                    @click="cancelSubscription()"
                  >{{ use_language.cancel }}</v-btn>
                  <v-btn
                    v-else
                    small color="info" depressed
                    :disabled="subForm.open"
                    @click="openSubForm()"
                  >
                    <v-icon left small>mdi-plus</v-icon>Subscription
                  </v-btn>
                </div>

                <template v-if="subForm.open">
                  <div class="px-4 pb-4">
                    <div class="fn-13 font-weight-medium mb-2">{{ use_language.monthly_fee_start_date }}</div>
                    <v-menu
                      v-model="subForm.menu"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      offset-y min-width="290px"
                    >
                      <template v-slot:activator="{ on }">
                        <v-text-field
                          :value="subForm.date"
                          outlined dense hide-details readonly
                          :placeholder="use_language.select_date"
                          prepend-inner-icon="mdi-calendar"
                          v-on="on"
                        />
                      </template>
                      <v-date-picker v-model="subForm.date" locale="th" @input="subForm.menu = false" />
                    </v-menu>
                    <div class="d-flex justify-end mt-3">
                      <v-btn small text class="mr-2" @click="subForm.open = false">{{ use_language.cancel }}</v-btn>
                      <v-btn
                        small color="info" depressed
                        :disabled="!subForm.date"
                        :loading="callDialog.subSaving"
                        @click="saveSubscription()"
                      >
                        <v-icon left small>mdi-content-save</v-icon>{{ use_language.button_save }}
                      </v-btn>
                    </div>
                  </div>
                </template>
              </v-card>

              <!-- ประวัติการโทร -->
              <v-card outlined>
                <div class="d-flex align-center px-4 py-2">
                  <v-icon small color="grey darken-2" class="mr-2">mdi-phone-log</v-icon>
                  <span class="fn-14 font-weight-medium">{{ use_language.record_history }}</span>
                  <v-spacer />
                  <v-btn small color="primary" depressed :disabled="callDialog.addOpen" @click="openAddCall()">
                    <v-icon left small>mdi-plus</v-icon>{{ use_language.add_call }}
                  </v-btn>
                </div>
                <v-divider />

                <!-- ฟอร์มเพิ่ม -->
                <div v-if="callDialog.addOpen" class="add-call pa-4">
                  <div class="fn-13 font-weight-medium mb-2">
                    {{ use_language.save_entry }}
                    <span class="color-grey fn-12">— {{ formatDateTime(nowText) }}</span>
                  </div>
                  <v-textarea
                    v-model="callDialog.newNote"
                    outlined rows="3" counter="500" maxlength="500" hide-details="auto"
                    :placeholder="use_language.eg_reorder_callback_noanswer"
                  />
                  <div class="d-flex justify-end mt-2">
                    <v-btn small text class="mr-2" @click="callDialog.addOpen = false">{{ use_language.cancel }}</v-btn>
                    <v-btn small color="primary" depressed :loading="callDialog.saving" @click="saveNewCall()">
                      <v-icon left small>mdi-content-save</v-icon>{{ use_language.button_save }}
                    </v-btn>
                  </div>
                </div>
                <v-divider v-if="callDialog.addOpen" />

                <div v-if="callDialog.callsLoading" class="pa-6 text-center">
                  <v-progress-circular indeterminate color="primary" size="28" />
                </div>
                <div v-else-if="!callDialog.calls.length" class="pa-6 text-center color-grey fn-13">
                  {{ use_language.no_call_history }}
                </div>
                <div v-else class="call-list">
                  <div v-for="c in callDialog.calls" :key="c.id" class="call-item">
                    <div class="d-flex align-center">
                      <v-icon x-small color="success" class="mr-2">mdi-phone-check</v-icon>
                      <span class="fn-13 font-weight-medium">{{ formatDateTime(c.calledDate) }}</span>
                      <v-spacer />
                      <v-chip v-if="c.orderMasterID" x-small outlined color="primary" class="mr-1">
                        PA{{ c.orderMasterID }}
                      </v-chip>
                      <span class="fn-12 color-grey">{{ c.calledByStaffName || '-' }}</span>
                    </div>
                    <div v-if="c.callNote" class="fn-13 mt-1 call-item__note">{{ c.callNote }}</div>
                  </div>
                </div>
              </v-card>
            </v-col>

            <!-- ============ ขวา: ออเดอร์ทั้งหมด ============ -->
            <v-col cols="12" md="5">
              <v-card outlined>
                <div class="d-flex align-center px-4 py-2">
                  <v-icon small color="grey darken-2" class="mr-2">mdi-package-variant-closed</v-icon>
                  <span class="fn-14 font-weight-medium">{{ use_language.all_orders }}</span>
                  <v-chip x-small class="ml-2">{{ callDialog.orders.length }}</v-chip>
                </div>
                <v-divider />

                <div v-if="callDialog.ordersLoading" class="pa-6 text-center">
                  <v-progress-circular indeterminate color="primary" size="28" />
                </div>
                <div v-else-if="!callDialog.orders.length" class="pa-6 text-center color-grey fn-13">
                  {{ use_language.order_not_found_label }}
                </div>
                <div v-else class="order-list">
                  <div v-for="o in callDialog.orders" :key="o.id" class="order-item">
                    <div class="d-flex align-center order-item__head" @click="toggleOrderExpand(o)">
                      <v-btn icon x-small class="mr-1">
                        <v-icon small>{{ o.expanded ? 'mdi-chevron-down' : 'mdi-chevron-right' }}</v-icon>
                      </v-btn>
                      <v-img
                        v-if="o.channelLogo"
                        :src="o.channelLogo"
                        width="22" height="22"
                        contain
                        class="order-item__logo mr-2 flex-grow-0 flex-shrink-0"
                      />
                      <div class="flex-grow-1" style="min-width:0;">
                        <div class="fn-13 font-weight-medium d-flex align-center">
                          PA{{ o.id }}
                          <v-btn
                            icon x-small class="ml-1"
                            :title="use_language.open_order_details"
                            @click.stop="openOrderDetail(o.id)"
                          >
                            <v-icon small color="primary">mdi-open-in-new</v-icon>
                          </v-btn>
                          <v-btn
                            icon x-small class="ml-1"
                            :title="use_language.reorder"
                            @click.stop="openReorderDialog(o)"
                          >
                            <v-icon small color="success">mdi-cart-plus</v-icon>
                          </v-btn>
                        </div>
                        <div class="fn-11 color-grey text-truncate">
                          {{ formatDate(o.createdDatetime) }} · {{ o.shippingStatusName || '-' }}
                        </div>
                      </div>
                      <div class="text-right flex-shrink-0 ml-2">
                        <div class="fn-13 font-weight-medium">{{ formatMoney(o.orderTotalAmount) }}</div>
                        <v-chip v-if="o.isCOD" x-small color="orange" outlined>COD</v-chip>
                      </div>
                    </div>

                    <!-- รายละเอียดสินค้า -->
                    <div v-if="o.expanded" class="order-item__body">
                      <div v-if="o.itemsLoading" class="py-3 text-center">
                        <v-progress-circular indeterminate color="primary" size="20" />
                      </div>
                      <div v-else-if="!o.items.length" class="py-3 text-center color-grey fn-12">
                        {{ use_language.product_list_not_found }}
                      </div>
                      <div v-else class="order-prod">
                        <div v-for="(p, pi) in o.items" :key="pi" class="order-prod__row d-flex">
                          <v-img
                            v-if="p.photoLink"
                            :src="p.photoLink"
                            width="60" height="60"
                            contain
                            class="order-prod__img flex-grow-0 flex-shrink-0 mr-2"
                          />
                          <div
                            v-else
                            class="order-prod__img d-flex align-center justify-center flex-grow-0 flex-shrink-0 mr-2"
                          >
                            <v-icon small color="grey lighten-1">mdi-image-off</v-icon>
                          </div>
                          <div class="flex-grow-1" style="min-width:0;">
                            <div class="order-prod__name" :title="p.name">{{ p.name }}</div>
                            <div class="d-flex align-center mt-1">
                              <span class="order-prod__sku flex-grow-1">SKU : {{ p.sku || '-' }}</span>
                              <span class="order-prod__qty">× {{ formatMoney(p.quantity) }}</span>
                              <span class="order-prod__price">{{ formatMoney(p.lineTotal) }}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </v-card>
            </v-col>
          </v-row>
        </v-card-text>

        <v-divider />
        <v-card-actions class="px-6 py-3">
          <v-spacer />
          <v-btn text @click="callDialog.show = false">{{ use_language.close }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ===== Dialog สั่งซื้อซ้ำ ===== -->
    <v-dialog v-model="reorderDialog.show" max-width="640px" scrollable persistent>
      <v-card class="call-dialog">
        <div class="call-dialog__header">
          <v-icon color="success" class="mr-2">mdi-cart-plus</v-icon>
          <span class="grey--text text--darken-4 font-weight-medium">{{ use_language.reorder }}</span>
          <v-spacer />
          <v-btn icon small color="grey darken-1" :disabled="reorderDialog.submitting" @click="reorderDialog.show = false"><v-icon small>mdi-close</v-icon></v-btn>
        </div>

        <v-card-text class="pt-4">
          <div v-if="reorderDialog.loading" class="pa-8 text-center">
            <v-progress-circular indeterminate color="primary" size="32" />
            <div class="fn-13 color-grey mt-3">{{ use_language.loading_order_data }}...</div>
          </div>
          <template v-else>
            <!-- ออเดอร์ต้นทาง -->
            <v-card outlined class="mb-3">
              <div class="d-flex align-center px-4 py-3">
                <v-img
                  v-if="reorderDialog.channelLogo"
                  :src="reorderDialog.channelLogo"
                  width="24" height="24" contain
                  class="mr-2 flex-grow-0 flex-shrink-0"
                />
                <div class="flex-grow-1" style="min-width:0;">
                  <div class="fn-14 font-weight-bold">{{ use_language.original_order }} PA{{ reorderDialog.orderMasterID }}</div>
                  <div class="fn-12 color-grey">{{ formatDate(reorderDialog.createdDatetime) }}</div>
                </div>
                <div class="text-right flex-shrink-0 ml-2">
                  <div class="fn-14 font-weight-bold">{{ formatMoney(reorderDialog.orderTotalAmount) }}</div>
                  <v-chip v-if="reorderDialog.isCOD" x-small color="orange" outlined>COD</v-chip>
                </div>
              </div>
            </v-card>

            <!-- ที่อยู่จัดส่งเดิม -->
            <v-card outlined class="mb-3">
              <div class="d-flex align-center px-4 py-2">
                <v-icon small color="grey darken-2" class="mr-2">mdi-map-marker</v-icon>
                <span class="fn-13 font-weight-medium">{{ use_language.delivery_address }}</span>
              </div>
              <v-divider />
              <div v-if="reorderDialog.shipping" class="px-4 py-2 fn-13">
                <div class="d-flex">
                  <span class="color-grey ship-label">{{ use_language.receiver_name }} :</span>
                  <span class="font-weight-medium">{{ reorderDialog.shipping.name || '-' }}</span>
                </div>
                <div class="d-flex mt-1">
                  <span class="color-grey ship-label">{{ use_language.tel }} :</span>
                  <span class="font-weight-medium">{{ reorderDialog.shipping.phone || '-' }}</span>
                </div>
                <div class="d-flex mt-1">
                  <span class="color-grey ship-label">{{ use_language.address }} :</span>
                  <span>{{ reorderShippingText || '-' }}</span>
                </div>
              </div>
              <div v-else class="px-4 py-3 fn-13 red--text">
                <v-icon small color="error" class="mr-1">mdi-alert</v-icon>
                {{ use_language.no_original_shipping_address }}
              </div>
            </v-card>

            <!-- รายการสินค้า -->
            <v-card outlined>
              <div class="d-flex align-center px-4 py-2">
                <v-icon small color="grey darken-2" class="mr-2">mdi-package-variant-closed</v-icon>
                <span class="fn-13 font-weight-medium">{{ use_language.reorder_product_list }}</span>
                <v-chip x-small class="ml-2">{{ reorderDialog.items.length }}</v-chip>
              </div>
              <v-divider />
              <div v-if="!reorderDialog.items.length" class="pa-4 text-center color-grey fn-13">{{ use_language.product_list_not_found }}</div>
              <div v-else class="order-prod px-4 py-2">
                <div v-for="(p, pi) in reorderDialog.items" :key="pi" class="order-prod__row d-flex">
                  <v-img
                    v-if="p.photoLink"
                    :src="p.photoLink"
                    width="60" height="60" contain
                    class="order-prod__img flex-grow-0 flex-shrink-0 mr-2"
                  />
                  <div
                    v-else
                    class="order-prod__img d-flex align-center justify-center flex-grow-0 flex-shrink-0 mr-2"
                  >
                    <v-icon small color="grey lighten-1">mdi-image-off</v-icon>
                  </div>
                  <div class="flex-grow-1" style="min-width:0;">
                    <div class="order-prod__name" :title="p.name">{{ p.name }}</div>
                    <div class="d-flex align-center mt-1">
                      <span class="order-prod__sku flex-grow-1">SKU : {{ p.sku || '-' }}</span>
                      <span class="order-prod__qty">× {{ formatMoney(p.quantity) }}</span>
                      <span class="order-prod__price">{{ formatMoney(p.lineTotal) }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </v-card>
          </template>
        </v-card-text>

        <v-divider />
        <v-card-actions class="px-6 py-3">
          <v-spacer />
          <v-btn text :disabled="reorderDialog.submitting" @click="reorderDialog.show = false">{{ use_language.cancel }}</v-btn>
          <v-btn
            color="success" depressed
            :loading="reorderDialog.submitting"
            :disabled="reorderDialog.loading || !reorderDialog.items.length"
            @click="submitReorder()"
          >
            <v-icon left small>mdi-cart-arrow-down</v-icon>{{ use_language.create_order }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Loading from '@/website/components/Loading'
import axios from 'axios'
import Swal from 'sweetalert2'
import { orderService_dotnet, shopService_dotnet } from '@/website/global'
import { format_price, get_languages } from '@/website/global_function'

export default {
  components: { Loading },
  data: () => ({
    use_language: {},
    header_token: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + localStorage.getItem('authorization_token') },
    page_loading: true,
    loading: false,

    ShopID: null,
    StaffShopID: null,
    isAllowShop: false,

    menuDateFrom: false,
    menuDateTo: false,
    filter: {
      searchText: null,
      callDateFrom: null,
      callDateTo: null,
      isCalled: null,        // default = ทั้งหมด
    },

    page: 1,
    perPage: 20,
    resultCount: 0,
    dataList: [],

    callDialog: {
      show: false, saving: false,
      id: null, shopCustomerID: null,
      customerName: null, customerPhone: null, orderMasterID: null,
      callsLoading: false,
      calls: [],
      calledCount: 0,
      orderTotalValue: 0,
      addOpen: false,
      newNote: null,
      ordersLoading: false,
      orders: [],
      isCalled: false,
      calledSaving: false,
      subscriptionStartDate: null,
      subSaving: false,
      callDate: null,
      callDateSaving: false,
    },

    reorderDialog: {
      show: false,
      loading: false,
      submitting: false,
      orderMasterID: null,
      channelLogo: null,
      createdDatetime: null,
      orderTotalAmount: null,
      isCOD: false,
      master: null,
      items: [],
      shipping: null,
    },

    subForm: { open: false, menu: false, date: null },
    callDateMenu: false,
    switchKey: 0,

    nowText: null,
  }),

  computed: {
    callStatusList() {
      return [
        { text: this.use_language.all_, value: null },
        { text: this.use_language.not_called, value: false },
        { text: this.use_language.called, value: true },
      ]
    },
    headers() {
      return [
        { text: this.use_language.number, value: 'no', align: 'center', sortable: false, width: '70px' },
        { text: this.use_language.menu_orders, value: 'orderMasterID', align: 'center', sortable: false, width: '100px' },
        { text: this.use_language.customer_name, value: 'customerName', align: 'left', sortable: false,width: '250px' },
        { text: this.use_language.tel, value: 'customerPhone', align: 'left', sortable: false, width: '100px' },
        { text: this.use_language.delivery_success_date, value: 'deliveredDate', align: 'center', sortable: false, width: '110px' },
        { text: this.use_language.expected_out_of_stock, value: 'expectedRunOutDate', align: 'center', sortable: false, width: '120px' },
        { text: this.use_language.date_should_call, value: 'callDate', align: 'center', sortable: false, width: '110px' },
        { text: this.use_language.remaining_short, value: 'daysUntilCall', align: 'center', sortable: false, width: '100px' },
        { text: this.use_language.previously_ordered, value: 'totalOrderCount', align: 'center', sortable: false, width: '80px' },
        { text: this.use_language.status, value: 'isCalled', align: 'center', sortable: false, width: '110px' },
        { text: '', value: 'action', align: 'center', sortable: false, width: '100px' },
      ]
    },
    pageLength() {
      if (this.resultCount <= 0) return 1
      return Math.ceil(this.resultCount / this.perPage)
    },
    // ค่าที่ส่งให้ v-date-picker (ต้องเป็น YYYY-MM-DD)
    callDatePickerValue() {
      if (!this.callDialog.callDate) return null
      const d = new Date(this.callDialog.callDate)
      if (isNaN(d.getTime())) return null
      const pad = n => ('0' + n).slice(-2)
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
    },
    // ที่อยู่จัดส่งเดิม (แบบข้อความ) สำหรับโชว์ใน dialog สั่งซ้ำ
    reorderShippingText() {
      const s = this.reorderDialog.shipping
      if (!s) return ''
      return [
        this.fld(s, 'address', 'Address'),
        this.fld(s, 'subDistrict', 'SubDistrict'),
        this.fld(s, 'district', 'District'),
        this.fld(s, 'province', 'Province'),
        this.fld(s, 'postcode', 'Postcode'),
      ].filter(x => x).join(' ')
    },
  },

  async created() {
    this.use_language = await this.get_languages(localStorage.getItem("set_language"));
    this.ShopID = localStorage.getItem('shop_id')
    this.StaffShopID = localStorage.getItem('staff_id')
    this.isAllowShop = (this.ShopID != null && this.ShopID.toString() === '8985')

    if (this.isAllowShop) {
      await this.searchData(1)
    }
    this.page_loading = false
  },

  methods: {
    get_languages,
    format_price,

    fld(obj, ...keys) {
      for (const k of keys) {
        if (obj && obj[k] !== undefined && obj[k] !== null) return obj[k]
      }
      return null
    },

    mapRow(r) {
      return {
        id: this.fld(r, 'id', 'ID'),
        shopCustomerID: this.fld(r, 'shopCustomerID', 'ShopCustomerID'),
        orderMasterID: this.fld(r, 'orderMasterID', 'OrderMasterID'),
        referenceNumber: this.fld(r, 'referenceNumber', 'ReferenceNumber'),
        customerName: this.fld(r, 'customerName', 'CustomerName'),
        customerPhone: this.fld(r, 'customerPhone', 'CustomerPhone'),
        deliveredDate: this.fld(r, 'deliveredDate', 'DeliveredDate'),
        expectedRunOutDate: this.fld(r, 'expectedRunOutDate', 'ExpectedRunOutDate'),
        callDate: this.fld(r, 'callDate', 'CallDate'),
        isCalled: this.fld(r, 'isCalled', 'IsCalled') === true,
        calledDate: this.fld(r, 'calledDate', 'CalledDate'),
        callNote: this.fld(r, 'callNote', 'CallNote'),
        subscriptionStartDate: this.fld(r, 'subscriptionStartDate', 'SubscriptionStartDate'),
        daysUntilCall: this.fld(r, 'daysUntilCall', 'DaysUntilCall'),
        totalOrderCount: this.fld(r, 'totalOrderCount', 'TotalOrderCount') || 0,
        calledByStaffShopID: this.fld(r, 'calledByStaffShopID', 'CalledByStaffShopID'),
        calledByStaffName: this.fld(r, 'calledByStaffName', 'CalledByStaffName'),
      }
    },

    formatMoney(v) {
      if (v == null) return '0'
      return v.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    },

    formatDate(d) {
      if (!d) return '-'
      const dt = new Date(d)
      if (isNaN(dt.getTime())) return '-'
      const dd = ('0' + dt.getDate()).slice(-2)
      const mm = ('0' + (dt.getMonth() + 1)).slice(-2)
      const yyyy = dt.getFullYear()
      return `${dd}/${mm}/${yyyy}`
    },

    callDateClass(item) {
      if (item.isCalled) return ''
      if (item.daysUntilCall < 0) return 'red--text font-weight-bold'
      if (item.daysUntilCall <= 3) return 'orange--text font-weight-bold'
      return ''
    },

    daysTextClass(item) {
      if (item.isCalled) return 'grey--text'
      if (item.daysUntilCall < 0) return 'red--text font-weight-bold'
      if (item.daysUntilCall <= 3) return 'orange--text text--darken-2 font-weight-bold'
      return 'green--text text--darken-1 font-weight-medium'
    },

    daysText(item) {
      const d = item.daysUntilCall
      if (d < 0) return this.use_language.at_all + ' ' + Math.abs(d) + ' ' + this.use_language.day
      if (d === 0) return this.use_language.today
      return this.use_language.more_word + ' ' + d + ' ' + this.use_language.day
    },

    openOrderDetail(orderMasterID) {
      if (!orderMasterID) return
      window.open('order-detail?id=' + orderMasterID, '_blank')
    },

    clearFilter() {
      this.filter.searchText = null
      this.filter.callDateFrom = null
      this.filter.callDateTo = null
      this.filter.isCalled = null
      this.searchData(1)
    },

    // ===== API 1: list =====
    async searchData(page) {
      if (!this.isAllowShop) return
      this.loading = true
      this.page = page || 1
      try {
        const res = await axios.post(
          orderService_dotnet + 'Order/get-reorder-reminder-list',
          {
            shopID: parseInt(this.ShopID),
            searchText: this.filter.searchText,
            callDateFrom: this.filter.callDateFrom,
            callDateTo: this.filter.callDateTo,
            isCalled: this.filter.isCalled,
            skip: (this.page - 1) * this.perPage,
            take: this.perPage,
          },
          { headers: this.header_token }
        )
        if (res.status === 200 && res.data.status === 'success') {
          const rows = this.fld(res.data, 'resultData', 'ResultData') || []
          this.dataList = rows.map(r => this.mapRow(r))
          this.resultCount = this.fld(res.data, 'resultCount', 'ResultCount') || 0
        } else {
          this.dataList = []
          this.resultCount = 0
          Swal.fire({ position: 'top', type: 'error', title: res.data.error_description || this.use_language.pickface_search_failed })
        }
      } catch (e) {
        this.dataList = []
        this.resultCount = 0
        Swal.fire({ position: 'top', type: 'error', title: this.use_language.search_error_occurred })
      }
      this.loading = false
    },

    openCallDialog(item) {
      this.callDialog.show = true
      this.callDialog.saving = false
      this.callDialog.id = item.id
      this.callDialog.shopCustomerID = item.shopCustomerID
      this.callDialog.customerName = item.customerName
      this.callDialog.customerPhone = item.customerPhone
      this.callDialog.orderMasterID = item.orderMasterID
      this.callDialog.addOpen = false
      this.callDialog.newNote = null
      this.callDialog.calls = []
      this.callDialog.calledCount = 0
      this.callDialog.orderTotalValue = 0
      this.callDialog.orders = []
      this.callDialog.subscriptionStartDate = item.subscriptionStartDate || null
      this.callDialog.subSaving = false
      this.callDialog.isCalled = item.isCalled === true
      this.callDialog.calledSaving = false
      this.callDialog.callDate = item.callDate || null
      this.callDialog.callDateSaving = false
      this.callDateMenu = false
      this.subForm = { open: false, menu: false, date: null }

      this.loadCustomerCalls()
      this.loadCustomerOrders()
    },

    async setIsCalled(value) {
      const next = value === true

      const confirm = await Swal.fire({
        position: 'top',
        type: next ? 'question' : 'warning',
        title: next ? this.use_language.log_call : this.use_language.cancel_call_log,
        showCancelButton: true,
        confirmButtonText: this.use_language.confirm,
        cancelButtonText: this.use_language.cancel,
        confirmButtonColor: '#1976d2',
      })
      if (!confirm.value) {
        this.switchKey++
        return
      }

      this.callDialog.calledSaving = true
      try {
        const res = await axios.post(
          orderService_dotnet + 'Order/update-reorder-reminder-called',
          { id: this.callDialog.id, isCalled: next },
          { headers: this.header_token }
        )
        if (res.status === 200 && res.data.status === 'success') {
          this.callDialog.isCalled = next
          await this.searchData(this.page)
        } else {
          Swal.fire({ position: 'top', type: 'error', title: res.data.error_description || this.use_language.ax_save_failed })
        }
      } catch (e) {
        Swal.fire({ position: 'top', type: 'error', title: this.use_language.error_saving })
      }
      this.callDialog.calledSaving = false
    },

    async onCallDatePicked(date) {
      this.callDateMenu = false
      if (!date || !this.callDialog.id) return

      const confirm = await Swal.fire({
        position: 'top',
        type: 'question',
        title: this.use_language.confirm_change_call_date,
        html: this.use_language.from_ + ' <b>' + this.formatDate(this.callDialog.callDate) + '</b> ' + this.use_language.change_to + ' <b>' + this.formatDate(date) + '</b> ?',
        showCancelButton: true,
        confirmButtonText: this.use_language.confirm,
        cancelButtonText: this.use_language.cancel,
        confirmButtonColor: '#1976d2',
      })
      if (!confirm.value) return

      this.callDialog.callDateSaving = true
      try {
        const res = await axios.post(
          orderService_dotnet + 'Order/update-reorder-reminder-calldate',
          { id: this.callDialog.id, callDate: date },
          { headers: this.header_token }
        )
        if (res.status === 200 && res.data.status === 'success') {
          this.callDialog.callDate = date
          Swal.fire({ position: 'top', type: 'success', title: this.use_language.call_date_saved, showConfirmButton: false, timer: 1200 })
          await this.searchData(this.page)
        } else {
          Swal.fire({ position: 'top', type: 'error', title: res.data.error_description || this.use_language.ax_save_failed })
        }
      } catch (e) {
        Swal.fire({ position: 'top', type: 'error', title: this.use_language.error_saving })
      }
      this.callDialog.callDateSaving = false
    },

    openSubForm() {
      const d = new Date()
      const pad = n => ('0' + n).slice(-2)
      this.subForm = {
        open: true,
        menu: false,
        date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
      }
    },

    async saveSubscription() {
      await this.postSubscription(this.subForm.date)
    },

    async cancelSubscription() {
      await this.postSubscription(null)
    },

    async postSubscription(dateOrNull) {
      this.callDialog.subSaving = true
      try {
        const res = await axios.post(
          orderService_dotnet + 'Order/update-reorder-reminder-subscription',
          {
            shopCustomerID: this.callDialog.shopCustomerID,
            subscriptionStartDate: dateOrNull,
          },
          { headers: this.header_token }
        )
        if (res.status === 200 && res.data.status === 'success') {
          this.callDialog.subscriptionStartDate = dateOrNull
          this.subForm.open = false
          Swal.fire({
            position: 'top', type: 'success', showConfirmButton: false, timer: 1200,
            title: dateOrNull ? this.use_language.subscription_registered : this.use_language.subscription_cancelled,
          })
          await this.searchData(this.page)
        } else {
          Swal.fire({ position: 'top', type: 'error', title: res.data.error_description || this.use_language.ax_save_failed })
        }
      } catch (e) {
        Swal.fire({ position: 'top', type: 'error', title: this.use_language.error_saving })
      }
      this.callDialog.subSaving = false
    },

    formatDateTime(d) {
      if (!d) return '-'
      const dt = new Date(d)
      if (isNaN(dt.getTime())) return '-'
      const pad = n => ('0' + n).slice(-2)
      return `${pad(dt.getDate())}/${pad(dt.getMonth() + 1)}/${dt.getFullYear()} ` +
             `${pad(dt.getHours())}:${pad(dt.getMinutes())}`
    },

    // ===== ประวัติการโทรของลูกค้า (รวมทุก reminder) =====
    async loadCustomerCalls() {
      this.callDialog.callsLoading = true
      try {
        const res = await axios.get(
          orderService_dotnet + 'Order/get-reorder-reminder-history',
          {
            params: { shopID: parseInt(this.ShopID), shopCustomerID: this.callDialog.shopCustomerID },
            headers: this.header_token,
          }
        )
        let calls = []
        if (res.status === 200 && res.data.status === 'success') {
          this.callDialog.calledCount = this.fld(res.data, 'calledCount', 'CalledCount') || 0
          this.callDialog.orderTotalValue = this.fld(res.data, 'orderTotalValue', 'OrderTotalValue') || 0
          const list = this.fld(res.data, 'calls', 'Calls') || []
          calls = list.map(c => ({
            id: this.fld(c, 'id', 'ID'),
            orderMasterID: this.fld(c, 'orderMasterID', 'OrderMasterID'),
            calledDate: this.fld(c, 'calledDate', 'CalledDate'),
            calledByStaffName: this.fld(c, 'calledByStaffName', 'CalledByStaffName'),
            callNote: this.fld(c, 'callNote', 'CallNote'),
          }))
          calls.sort((a, b) => new Date(b.calledDate) - new Date(a.calledDate))
        }
        this.callDialog.calls = calls
      } catch (e) {
        this.callDialog.calls = []
        Swal.fire({ position: 'top', type: 'error', title: this.use_language.load_call_history_failed })
      }
      this.callDialog.callsLoading = false
    },

    // ===== ออเดอร์ทั้งหมดของลูกค้า =====
    async loadCustomerOrders() {
      this.callDialog.ordersLoading = true
      try {
        const res = await axios.post(
          orderService_dotnet + 'Order/get-order-list-for-chat-page',
          {
            shopID: parseInt(this.ShopID),
            shopCustomerID: this.callDialog.shopCustomerID,
            skip: 0,
            take: 100,
          },
          { headers: this.header_token }
        )
        const rows = this.fld(res.data, 'orderList', 'OrderList') || []
        this.callDialog.orders = rows.map(o => ({
          id: this.fld(o, 'id', 'ID'),
          createdDatetime: this.fld(o, 'createdDatetime', 'CreatedDatetime'),
          orderTotalAmount: this.fld(o, 'orderTotalAmount', 'OrderTotalAmount'),
          shippingStatusName: this.fld(o, 'shippingStatusName', 'ShippingStatusName'),
          trackingNo: this.fld(o, 'trackingNo', 'TrackingNo'),
          channelLogo: this.fld(o, 'channelLogo', 'ChannelLogo'),
          isCOD: this.fld(o, 'isCOD', 'IsCOD') === true,
          expanded: false,
          itemsLoading: false,
          itemsLoaded: false,
          items: [],
        }))
      } catch (e) {
        this.callDialog.orders = []
        Swal.fire({ position: 'top', type: 'error', title: this.use_language.load_order_failed })
      }
      this.callDialog.ordersLoading = false
    },

    // ===== กด expand ค่อยยิงดูสินค้าในออเดอร์ =====
    async toggleOrderExpand(o) {
      o.expanded = !o.expanded
      if (!o.expanded || o.itemsLoaded) return

      o.itemsLoading = true
      try {
        const res = await axios.post(
          orderService_dotnet + 'Package/get-order-package-info',
          { orderID: o.id },
          { headers: this.header_token }
        )
        const list = this.fld(res.data, 'orderItemList', 'OrderItemList') || []
        o.items = list.map(i => {
          const quantity = this.fld(i, 'quantity', 'Quantity') || 0
          const unitPrice = this.fld(i, 'unitPrice', 'UnitPrice') || 0
          const discount = this.fld(i, 'discount', 'Discount') || 0
          return {
            name: this.fld(i, 'productName', 'ProductName', 'name', 'Name') || '-',
            sku: this.fld(i, 'sku', 'Sku', 'SKU'),
            photoLink: this.fld(i, 'photoLink', 'PhotoLink'),
            quantity,
            lineTotal: Math.max(0, unitPrice * quantity - discount),
          }
        })
        o.itemsLoaded = true
      } catch (e) {
        o.items = []
        Swal.fire({ position: 'top', type: 'error', title: this.use_language.load_order_detail_failed })
      }
      o.itemsLoading = false
    },

    openAddCall() {
      this.callDialog.newNote = null
      this.callDialog.addOpen = true
      this.nowText = new Date().toISOString()
    },

    // ===== บันทึกการโทรครั้งใหม่ (เพิ่มแถวใน ShopCustomerCall) =====
    async saveNewCall() {
      this.callDialog.saving = true
      try {
        const res = await axios.post(
          orderService_dotnet + 'Order/add-reorder-reminder-call',
          {
            id: this.callDialog.id,                       // null ได้ ถ้ายังไม่มีรายการติดตาม
            shopCustomerID: this.callDialog.shopCustomerID,
            callNote: this.callDialog.newNote,
            staffShopID: parseInt(this.StaffShopID || 0),
          },
          { headers: this.header_token }
        )
        if (res.status === 200 && res.data.status === 'success') {
          this.callDialog.addOpen = false
          this.callDialog.newNote = null
          // บันทึกการโทรได้ = โทรแล้ว (backend ติ๊กให้ด้วย)
          if (this.callDialog.id) this.callDialog.isCalled = true
          Swal.fire({ position: 'top', type: 'success', title: this.use_language.save_complete, showConfirmButton: false, timer: 1200 })
          await this.loadCustomerCalls()
          await this.searchData(this.page)
        } else {
          Swal.fire({ position: 'top', type: 'error', title: res.data.error_description || this.use_language.ax_save_failed })
        }
      } catch (e) {
        Swal.fire({ position: 'top', type: 'error', title: this.use_language.error_saving })
      }
      this.callDialog.saving = false
    },

    // ===== สั่งซื้อซ้ำ =====
    uuidv4() {
      return ('10000000-1000-4000-8000-100000000000').replace(/[018]/g, c =>
        (c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16)
      )
    },

    async openReorderDialog(o) {
      this.reorderDialog.show = true
      this.reorderDialog.loading = true
      this.reorderDialog.submitting = false
      this.reorderDialog.orderMasterID = o.id
      this.reorderDialog.channelLogo = o.channelLogo
      this.reorderDialog.createdDatetime = o.createdDatetime
      this.reorderDialog.orderTotalAmount = o.orderTotalAmount
      this.reorderDialog.isCOD = o.isCOD === true
      this.reorderDialog.master = null
      this.reorderDialog.items = []
      this.reorderDialog.shipping = null

      try {
        const [masterRes, itemRes] = await Promise.all([
          axios.post(orderService_dotnet + 'Order/get-order-master', { orderID: o.id }, { headers: this.header_token }),
          axios.post(orderService_dotnet + 'Order/get-order-item', { orderID: o.id }, { headers: this.header_token }),
        ])

        const master = masterRes.data || {}
        this.reorderDialog.master = master

        const rawItems = Array.isArray(itemRes.data)
          ? itemRes.data
          : (this.fld(itemRes.data, 'orderItemList', 'OrderItemList') || [])
        this.reorderDialog.items = rawItems.map(i => {
          const quantity = this.fld(i, 'quantity', 'Quantity') || 0
          const unitPrice = this.fld(i, 'unitPrice', 'UnitPrice') || 0
          const discount = this.fld(i, 'discount', 'Discount') || 0
          return {
            stockShopId: this.fld(i, 'stockShopId', 'StockShopId'),
            productMasterId: this.fld(i, 'productMasterId', 'ProductMasterId'),
            productMasterItemId: this.fld(i, 'productMasterItemId', 'ProductMasterItemId'),
            quantity,
            unitPrice,
            discount,
            discountPercent: this.fld(i, 'discountPercent', 'DiscountPercent') || 0,
            name: this.fld(i, 'productName', 'ProductName', 'name', 'Name') || '-',
            sku: this.fld(i, 'sku', 'Sku', 'SKU'),
            photoLink: this.fld(i, 'photoLink', 'PhotoLink'),
            lineTotal: Math.max(0, unitPrice * quantity - discount),
          }
        })

        // ดึงที่อยู่จัดส่งเดิม (มี postcodeMappingId ครบ) จาก shop service
        const shippingAddressID = this.fld(master, 'shippingAddressID', 'ShippingAddressID', 'shippingAddressId')
        if (shippingAddressID) {
          try {
            const shipRes = await axios.post(
              shopService_dotnet + 'Shop/get-shipping-address-list',
              { id: shippingAddressID, isAscending: true },
              { headers: this.header_token }
            )
            const arr = Array.isArray(shipRes.data)
              ? shipRes.data
              : (this.fld(shipRes.data, 'resultData', 'ResultData') || [])
            this.reorderDialog.shipping = (arr && arr.length) ? arr[0] : null
          } catch (e) {
            this.reorderDialog.shipping = null
          }
        }
      } catch (e) {
        this.reorderDialog.show = false
        Swal.fire({ position: 'top', type: 'error', title: this.use_language.load_order_data_failed })
      }
      this.reorderDialog.loading = false
    },

    async submitReorder() {
      const d = this.reorderDialog
      if (!d.items.length) return

      const confirm = await Swal.fire({
        position: 'top',
        type: 'question',
        title: this.use_language.confirm_create_new_order,
        html: this.use_language.reorder_from + ' <b>PA' + d.orderMasterID + '</b> ' + this.use_language.quantity + ' <b>' + d.items.length + '</b> ' + this.use_language.item_unit + ' ?',
        showCancelButton: true,
        confirmButtonText: this.use_language.create_order_btn,
        cancelButtonText: this.use_language.cancel,
        confirmButtonColor: '#43a047',
      })
      if (!confirm.value) return

      d.submitting = true
      try {
        const m = d.master || {}
        const shopId = parseInt(this.ShopID)
        const staffId = parseInt(this.StaffShopID || 0) || null

        // ต้องมี stockShopId ถึงจะสั่งซ้ำได้
        const orderItem = d.items
          .filter(it => it.stockShopId)
          .map(it => ({
            id: 0,
            orderMasterId: 0,
            stockShopId: it.stockShopId,
            quantity: it.quantity,
            unitPrice: it.unitPrice,
            salePrice: it.unitPrice,
            discountPercent: it.discountPercent || 0,
            discount: it.discount || 0,
            isLock: true,
            productMasterID: it.productMasterId,
            productMasterItemID: it.productMasterItemId,
            isProductSet: false,
            name: null,
          }))

        if (!orderItem.length) {
          d.submitting = false
          Swal.fire({ position: 'top', type: 'error', title: this.use_language.no_reorderable_products + ' (ไม่พบข้อมูลสต็อก)' })
          return
        }

        const totalPrice = orderItem.reduce(
          (s, it) => s + Math.max(0, it.unitPrice * it.quantity - it.discount), 0
        )

        const orderMaster = {
          shopId,
          branchId: this.fld(m, 'branchID', 'BranchID', 'branchId') || null,
          createdByStaffId: staffId,
          salemanId: this.fld(m, 'salemanID', 'SalemanID') || staffId,
          shippingStatusId: 0,
          totalPrice,
          deliveryCost: this.fld(m, 'deliveryCost', 'DeliveryCost') || 0,
          orderDiscountPercent: null,
          orderDiscount: this.fld(m, 'orderDiscount', 'OrderDiscount') || null,
          vatType: this.fld(m, 'vatType', 'VatType', 'VATType'),
          vatpercent: this.fld(m, 'vatPercent', 'VATPercent', 'vatpercent'),
          vatAmount: this.fld(m, 'vatAmount', 'VatAmount'),
          shopAddressId: this.fld(m, 'shopAddressID', 'ShopAddressID') || null,
          shippingAddressId: null,
          shippingAddressInvoiceId: null,
          isCOD: this.fld(m, 'isCOD', 'IsCOD') === true,
          codamount: this.fld(m, 'codAmount', 'CODAmount', 'codamount') || null,
          insuranceAmount: null,
          remark: this.use_language.reorder_from + ' PA' + d.orderMasterID,
          remarkCustomer: this.fld(m, 'noteForCustomer', 'NoteForCustomer') || '',
          remarkWarehouse: this.fld(m, 'noteForWarehouse', 'NoteForWarehouse') || '',
          shopBankAccountId: this.fld(m, 'shopBankAccountID', 'ShopBankAccountID') || null,
          expressCompanyId: this.fld(m, 'expressCompanyID', 'ExpressCompanyID') || null,
          shopExpressID: this.fld(m, 'shopExpressID', 'ShopExpressID') || null,
          branchExpressID: this.fld(m, 'branchExpressID', 'BranchExpressID') || null,
          referenceSourceId: this.fld(m, 'referenceSourceID', 'ReferenceSourceID') || 0,
          warehouseId: this.fld(m, 'warehouseID', 'WarehouseID') || null,
          referenceNumber: null,
          taxID: null,
          createToken: this.uuidv4(),
        }

        let shippingAddress = null
        if (d.shipping) {
          const s = d.shipping
          shippingAddress = {
            id: 0,
            shopId,
            name: this.fld(s, 'name', 'Name') || '',
            phone: this.fld(s, 'phone', 'Phone') || '',
            address: this.fld(s, 'address', 'Address') || '',
            postcodeMappingId: this.fld(s, 'postcodeMappingId', 'PostcodeMappingId') || 0,
            subDistrict: this.fld(s, 'subDistrict', 'SubDistrict') || '',
            district: this.fld(s, 'district', 'District') || '',
            provinceId: this.fld(s, 'provinceId', 'ProvinceId') || 0,
            province: this.fld(s, 'province', 'Province') || '',
            postcode: this.fld(s, 'postcode', 'Postcode') || '',
            shopCustomerID: this.fld(s, 'shopCustomerID', 'ShopCustomerID') || this.callDialog.shopCustomerID || 0,
            customerFacebook: this.fld(s, 'customerFacebook', 'CustomerFacebook') || '',
            customerLine: this.fld(s, 'customerLine', 'CustomerLine') || '',
            customerEmail: this.fld(s, 'customerEmail', 'CustomerEmail') || '',
          }
        }

        const res = await axios.post(
          orderService_dotnet + 'Order/new-order',
          {
            orderMaster,
            orderItem,
            shopBankAccount: null,
            shippingAddress,
            shippingAddressInvoice: null,
            shopAddress: null,
            orderPayment: null,
            orderDoc: null,
            b2bInsert: null,
          },
          { headers: this.header_token }
        )

        if (res.status === 200 && res.data.status === 'success') {
          const newId = this.fld(res.data, 'orderID', 'OrderID', 'orderId')
          d.show = false
          Swal.fire({
            position: 'top', type: 'success',
            title: this.use_language.new_order_created_success + (newId ? ' PA' + newId : ''),
            showConfirmButton: false, timer: 1400,
          })
          if (newId) this.openOrderDetail(newId)
          await this.loadCustomerOrders()
        } else {
          Swal.fire({ position: 'top', type: 'error', title: res.data.error_description || this.use_language.create_order_failed })
        }
      } catch (e) {
        Swal.fire({ position: 'top', type: 'error', title: this.use_language.error_creating_order })
      }
      d.submitting = false
    },
  },
}
</script>

<style scoped>
.packhai-border-table {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
}
.color-grey {
  color: #9e9e9e;
}
.fn-11 { font-size: 11px; }
.fn-12 { font-size: 12px; }
.fn-13 { font-size: 13px; }

.ship-label {
  flex: 0 0 70px;
  width: 70px;
}

.sub-row--on {
  background: #e3f2fd;
}

.cust-line {
  display: block;
  line-height: 1.9;
}

.add-call {
  background: #f5f9ff;
}
.call-list {
  max-height: 340px;
  overflow-y: auto;
}
.call-item {
  padding: 10px 16px;
  border-bottom: 1px solid #eceff1;
}
.call-item:last-child {
  border-bottom: none;
}
.call-item__note {
  color: #546e7a;
  white-space: pre-wrap;
  word-break: break-word;
}

.order-list {
  max-height: 480px;
  overflow-y: auto;
}
.order-item {
  border-bottom: 1px solid #eceff1;
}
.order-item:last-child {
  border-bottom: none;
}
.order-item__head {
  padding: 8px 12px;
  cursor: pointer;
}
.order-item__head:hover {
  background: #fafafa;
}
.order-item__logo {
  flex: 0 0 22px;
}
.order-item__body {
  padding: 4px 12px 10px 36px;
  background: #fafafa;
}
.order-prod__img {
  width: 40px;
  height: 40px;
  border: 1px solid #eceff1;
  border-radius: 4px;
  background: #fafafa;
}
.order-prod__row {
  padding: 7px 0;
  border-top: 1px dashed #e0e0e0;
}
.order-prod__row:first-child {
  border-top: none;
  padding-top: 2px;
}

.order-prod__name {
  font-size: 12px;
  line-height: 1.35;
  color: #37474f;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.order-prod__sku {
  font-size: 11px;
  color: #90a4ae;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.order-prod__qty {
  font-size: 12px;
  color: #607d8b;
  white-space: nowrap;
  margin-right: 14px;
}
.order-prod__price {
  font-size: 12px;
  font-weight: 600;
  min-width: 62px;
  text-align: right;
  white-space: nowrap;
}
.fn-14 { font-size: 14px; }
.fn-15 { font-size: 15px; }
.fn-16 { font-size: 16px; }

.call-dialog {
  border-radius: 10px;
  overflow: hidden;
}
.call-dialog__header {
  display: flex;
  align-items: center;
  padding: 14px 20px;
  background: #f5f6f8;
  border-bottom: 1px solid #e3e7ec;
  font-size: 16px;
}
.call-dialog__customer {
  display: flex;
  align-items: center;
  background: #f7f9fc;
  border: 1px solid #e8edf3;
  border-radius: 8px;
  padding: 12px 14px;
}
.call-dialog__status {
  display: flex;
  align-items: center;
  border-radius: 8px;
  padding: 12px 14px;
  border: 1px solid #e0e0e0;
  transition: background-color 0.2s ease, border-color 0.2s ease;
}
.call-dialog__status.is-called {
  background: #f1f8f3;
  border-color: #c8e6c9;
}
.call-dialog__status.not-called {
  background: #fafafa;
  border-color: #e0e0e0;
}

</style>